import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';

export interface ProtectedDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
}

/**
 * Protected Delete Dialog Component
 *
 * A confirmation dialog that requires users to type a specific text
 * to confirm deletion. Designed for production safety on critical operations.
 *
 * @param open - Whether the dialog is open
 * @param onClose - Callback when dialog is closed without confirming
 * @param onConfirm - Callback when deletion is confirmed
 * @param title - Dialog title
 * @param message - Warning message to display
 * @param confirmText - Text user must type to confirm (default: "delete prod")
 */
export const ProtectedDeleteDialog: React.FC<ProtectedDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'delete prod',
}) => {
  const [inputValue, setInputValue] = useState('');

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setInputValue('');
    }
  }, [open]);

  const isConfirmEnabled = inputValue === confirmText;

  const handleConfirm = () => {
    if (isConfirmEnabled) {
      onConfirm();
      setInputValue(''); // Reset for next time
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isConfirmEnabled) {
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: '3px solid',
          borderColor: 'error.main',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={24} color="#f44336" />
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {message}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Type <strong>{confirmText}</strong> to confirm:
        </Typography>

        <TextField
          autoFocus
          fullWidth
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={confirmText}
          variant="outlined"
          error={inputValue.length > 0 && !isConfirmEnabled}
          helperText={
            inputValue.length > 0 && !isConfirmEnabled
              ? 'Text does not match'
              : ''
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!isConfirmEnabled}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
